import React, { useState } from 'react';
import { ChevronUp, Check } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import type { ToolStep } from './types';

export interface HorizontalStepperProps {
  steps: ToolStep[];
  currentStepKey: string;
  completedKeys: Set<string>;
  onStepChange: (key: string) => void;
}

/** Small-screen-only step indicator. Renders below the toolbar, above the
 *  workspace content. Shows ONE chip ("Step 4 of 9 · Filter") that taps to
 *  expand a bottom-sheet listing every step + its status — per designer's
 *  mobile rules ("don't try to fit 9 chips in a row — show one, expand on tap"). */
export const HorizontalStepper: React.FC<HorizontalStepperProps> = ({
  steps,
  currentStepKey,
  completedKeys,
  onStepChange,
}) => {
  const [open, setOpen] = useState(false);
  const stepIndex = steps.findIndex((s) => s.key === currentStepKey);
  const currentStep = steps[stepIndex];
  if (!currentStep) return null;
  const isLast = stepIndex === steps.length - 1;

  const handlePick = (key: string) => {
    setOpen(false);
    onStepChange(key);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="md:hidden flex items-center justify-between w-full text-left transition-colors"
          style={{
            padding: '12px 16px',
            background: 'var(--hj-surface-2)',
            borderBottom: '1px solid var(--hj-hairline-soft)',
            color: 'var(--hj-ink-2)',
            border: 0,
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: 48,
          }}
          aria-label="Show all pipeline steps"
        >
          <span className="flex items-center gap-2.5 min-w-0">
            <span
              aria-hidden
              className="flex items-center justify-center rounded-full flex-shrink-0"
              style={{
                width: 22,
                height: 22,
                background: 'var(--hj-yellow)',
                color: 'oklch(0.16 0.005 70)',
                fontFamily: 'var(--hj-font-mono)',
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              {String(stepIndex + 1).padStart(2, '0')}
            </span>
            <span className="truncate" style={{ color: 'var(--hj-ink)', fontSize: 14 }}>
              <span className="mono-label" style={{ fontSize: 10, marginRight: 8 }}>
                Step {stepIndex + 1} of {steps.length}
              </span>
              <span style={{ fontWeight: 600 }}>{currentStep.label}</span>
            </span>
          </span>
          <ChevronUp size={16} strokeWidth={2} style={{ color: 'var(--hj-ink-3)' }} />
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="p-0"
        style={{
          background: 'var(--hj-surface-1)',
          borderColor: 'var(--hj-hairline)',
          color: 'var(--hj-ink)',
          maxHeight: '70vh',
        }}
      >
        <div style={{ padding: '20px' }}>
          <div className="mono-label" style={{ marginBottom: 12, color: 'var(--hj-ink-4)' }}>
            Pipeline · Step {stepIndex + 1} of {steps.length}
            {isLast && ' · Last step'}
          </div>
          <nav className="flex flex-col" style={{ gap: 2 }}>
            {steps.map((s, i) => {
              const isCurrent = s.key === currentStepKey;
              const isDone = completedKeys.has(s.key);
              const unlocked =
                i === 0 ||
                isDone ||
                isCurrent ||
                i <= stepIndex ||
                (i > 0 && completedKeys.has(steps[i - 1].key));
              return (
                <button
                  key={s.key}
                  type="button"
                  disabled={!unlocked}
                  onClick={() => unlocked && handlePick(s.key)}
                  className="grid items-center gap-3 text-left transition-colors disabled:cursor-not-allowed"
                  style={{
                    gridTemplateColumns: '28px 1fr 18px',
                    padding: '12px 12px',
                    borderRadius: 'var(--hj-r-sm)',
                    background: isCurrent ? 'var(--hj-surface-2)' : 'transparent',
                    border: 0,
                    color: isCurrent ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                    fontFamily: 'inherit',
                    cursor: unlocked ? 'pointer' : 'not-allowed',
                    opacity: unlocked ? 1 : 0.4,
                    minHeight: 44,
                  }}
                >
                  <span
                    className="tabular flex items-center justify-center rounded-full"
                    style={{
                      width: 26,
                      height: 26,
                      background: isDone
                        ? 'var(--hj-green)'
                        : isCurrent
                          ? 'var(--hj-yellow)'
                          : 'transparent',
                      border: isDone || isCurrent ? 'none' : '1px solid var(--hj-hairline)',
                      color: isDone || isCurrent ? 'oklch(0.16 0.005 70)' : 'var(--hj-ink-3)',
                      fontFamily: 'var(--hj-font-mono)',
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {isDone ? <Check size={11} strokeWidth={2.5} /> : String(i + 1).padStart(2, '0')}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: isCurrent ? 600 : 500 }}>
                    {s.label}
                  </span>
                  {isCurrent && (
                    <span
                      className="mono-label"
                      style={{ fontSize: 9, color: 'var(--hj-yellow-ink)' }}
                    >
                      Now
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );
};
